/**
 * Report Generator
 * Generates PDF reports of student progress using jsPDF
 */

class ReportGenerator {
    constructor() {
        this.doc = null;
    }

    /**
     * Generate and download session report
     * @param {Object} customProgress - Optional: specific student progress data
     */
    async generateSessionReport(customProgress = null) {
        if (!window.jspdf) {
            try {
                const jspdfModule = await import('jspdf');
                window.jspdf = { jsPDF: jspdfModule.jsPDF };
            } catch (err) {
                console.error('jsPDF library load failed:', err);
                alert('Error: No se pudo cargar la librería para generar PDF.');
                return;
            }
        }

        const { jsPDF } = window.jspdf;
        this.doc = new jsPDF();

        // Data gathering
        const progress = customProgress || (window.GamificationService ? window.GamificationService.getProgress() : null);
        if (!progress) {
            alert('No hay datos de sesión disponibles.');
            return;
        }

        const stats = progress.stats || {};
        const session = progress.sessionStats || { problemsThisSession: 0, xpGainedThisSession: 0 };
        const date = new Date().toLocaleDateString('es-ES', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        // --- PDF DESIGN ---

        // Header
        this.doc.setFillColor(99, 102, 241); // Primary Color (Indigo)
        this.doc.rect(0, 0, 210, 40, 'F');

        this.doc.setTextColor(255, 255, 255);
        this.doc.setFont('helvetica', 'bold');
        this.doc.setFontSize(22);
        this.doc.text('Informe de Sesión: Detectimáticas', 105, 15, { align: 'center' });

        this.doc.setFontSize(12);
        this.doc.setFont('helvetica', 'normal');
        this.doc.text(date, 105, 25, { align: 'center' });

        // User Info
        this.doc.setTextColor(0, 0, 0);
        this.doc.setFontSize(14);
        this.doc.text(`Nivel: ${progress.level}`, 20, 60);
        this.doc.text(`XP Total: ${progress.xp}`, 80, 60);
        this.doc.text(`Estrellas: ${progress.stars}`, 150, 60);

        // [NEW] Analytics Row
        const history = progress.sessionHistory || [];
        const validDurations = history.map(b => typeof b === 'number' ? b : b?.duration).filter(d => typeof d === 'number' && !isNaN(d) && d > 0);
        const totalTime = validDurations.reduce((a, b) => a + b, 0);
        const avgTime = validDurations.length > 0 ? Math.round(totalTime / validDurations.length) : 0;

        this.doc.setFont('helvetica', 'bold');
        this.doc.text(`Tiempo medio por problema: ${avgTime}s`, 20, 75);

        let efficiency = 'Iniciado';
        if (avgTime > 0) {
            if (avgTime < 25) efficiency = 'Experto (Muy Fluido)';
            else if (avgTime < 45) efficiency = 'Avanzado (Fluido)';
            else efficiency = 'En proceso (Mejorando)';
        }
        this.doc.setFontSize(12);
        this.doc.setFont('helvetica', 'normal');
        this.doc.text(`Nivel de Eficiencia: ${efficiency}`, 20, 82);

        // Session Summary Box
        this.doc.setDrawColor(200, 200, 200);
        this.doc.setFillColor(248, 250, 252);
        this.doc.roundedRect(15, 90, 180, 40, 3, 3, 'FD');

        this.doc.setFontSize(16);
        this.doc.setTextColor(75, 85, 99); // Gray 600
        this.doc.text('Resumen de Hoy', 25, 105);

        this.doc.setFontSize(12);
        this.doc.setTextColor(0, 0, 0);
        this.doc.text(`Problemas resueltos: ${session.problemsThisSession}`, 25, 118);
        this.doc.text(`XP ganada hoy: ${session.xpGainedThisSession}`, 100, 118);

        // Detailed Stats Table (Manual construction)
        let yPos = 150;
        this.doc.setFontSize(14);
        this.doc.setTextColor(99, 102, 241);
        this.doc.text('Detalle por Operación', 20, yPos);
        yPos += 10;

        // Table Header
        this.doc.setFillColor(229, 231, 235); // Gray 200
        this.doc.rect(20, yPos, 170, 10, 'F');
        this.doc.setFontSize(10);
        this.doc.setTextColor(0, 0, 0);
        this.doc.setFont('helvetica', 'bold');
        this.doc.text('OPERACIÓN', 25, yPos + 7);
        this.doc.text('INTENTOS', 85, yPos + 7);
        this.doc.text('ACIERTOS', 135, yPos + 7);
        this.doc.text('% ÉXITO', 170, yPos + 7);
        yPos += 10;

        // Rows
        const operations = [
            { id: 'suma', label: 'Suma' },
            { id: 'resta', label: 'Resta' },
            { id: 'combinada', label: 'Combinada' }
        ];

        this.doc.setFont('helvetica', 'normal');

        operations.forEach((op, index) => {
            const opStats = stats[op.id];
            if (opStats && opStats.attempted > 0) {
                const percent = Math.round((opStats.correct / opStats.attempted) * 100) + '%';

                // Zebra striping
                if (index % 2 === 0) {
                    this.doc.setFillColor(255, 255, 255);
                } else {
                    this.doc.setFillColor(249, 250, 251);
                }
                this.doc.rect(20, yPos, 170, 10, 'F');

                this.doc.text(op.label, 25, yPos + 7);
                this.doc.text(opStats.attempted.toString(), 85, yPos + 7);
                this.doc.text(opStats.correct.toString(), 135, yPos + 7);
                this.doc.text(percent, 170, yPos + 7);

                yPos += 10;
            }
        });

        // Encouraging Note at bottom
        this.doc.setFontSize(12);
        this.doc.setTextColor(100, 100, 100);
        this.doc.text('¡Sigue practicando para convertirte en un Experto Detective!', 105, 280, { align: 'center' });

        // Save
        const filename = `Informe_Detectives_${new Date().toISOString().split('T')[0]}.pdf`;
        this.doc.save(filename);
    }
}

// Expose global instance
window.ReportGenerator = new ReportGenerator();
